import React, { useState } from 'react';
import { IoMdSend } from "react-icons/io";
import IndicatorsPanel from '../IndicatorsPanel/IndicatorsPanel';
import Tooltip from '../Tooltip/Tooltip';
import VictoryConfetti from '../VictoryConfetti/VictoryConfetti';
import DataCorrectionModal from '../DataCorrectionModal/DataCorrectionModal';
import ShareResultsModal from '../ShareResultsModal/ShareResultsModal';
import useGameLogic from '../../hooks/useGameLogic';
import './GameEngine.scss';

/**
 * Generic game engine component that handles all common game functionality
 * Accepts configuration to customize for specific games
 */
const GameEngine = ({ config }) => {
  const [showDataCorrectionModal, setShowDataCorrectionModal] = useState(false);
  const [showShareResultsModal, setShowShareResultsModal] = useState(false);
  // Extract ALL logic to custom hook while preserving variable names
  const {
    // State variables
    items,
    currentItem,
    guessedItems,
    currentGuess,
    attempts,
    gameWon,
    justWon,
    showIndicators,
    isHintRevealed,
    showDirectory,
    gameKey,
    showHowToPlay,
    isDailyMode,
    dailyCompleted,
    todayDate,
    currentStreak,
    showDropdown,
    highlightedIndex,
    triggerConfetti,
    
    // Refs
    guessInputRef,
    dropdownRef,
    suggestionItemsRef,
    skipNextFocus,
    
    // Auth
    authLoading,
    user,
    isAuthenticated,
    
    // Callbacks
    handleInputChange,
    handleInputFocus,
    handleSuggestionClick,
    handleKeyDown,
    handleSubmit,
    resetGame,
    handleDirectorySelect,
    handleCloseIndicators,
    handleRevealHint,
    handleShowHowToPlay,
    handleCloseHowToPlay,
    handleShowDirectory,
    handleCloseDirectory,
    
    // Memoized values
    suggestions,
    showSuggestions,
    isValidGuess,
    
    // Props objects for child components
    directoryProps,
    hintProps,
    
    // Tooltip content
    dailyChallengeTooltip,
    endlessModeTooltip,
    
    // Config values needed in JSX
    cssPrefix,
    gameName,
    gameId,
    placeholderText,
    directoryButtonText,
    categories,
    itemName,
    getDisplayValue,
    renderVictoryDetails,
    renderHintContent,
    HowToPlayComponent,
    DirectoryComponent,
    HintComponent,
    
    // State setters for UI state
    setHighlightedIndex,
    setTriggerConfetti
  } = useGameLogic(config);

  return (
    <div className={cssPrefix}>
      <header className={`${cssPrefix}__header`}>
        <h1 className={`${cssPrefix}__title`}>{gameName}</h1>
        <div className={`${cssPrefix}__mode-container`}>
          {isDailyMode && (
            <>
              <Tooltip content={dailyChallengeTooltip} position="bottom">
                <div className={`${cssPrefix}__daily-indicator`}>
                  <span>Daily Challenge - {todayDate}</span>
                </div>
              </Tooltip>
              <div className={`${cssPrefix}__streak-container`}>
                Daily Streak: <span className={`${cssPrefix}__streak-count`}>{currentStreak}</span>
                <img src="/flame-white-on-transparent-2.png" alt="Streak flame" className={`${cssPrefix}__streak-flame`} />
              </div>
            </>
          )}
          {!isDailyMode && (
            <Tooltip content={endlessModeTooltip} position="bottom">
              <div className={`${cssPrefix}__endless-indicator`}>
                <span>Endless Mode</span>
              </div>
            </Tooltip>
          )}
        </div>
        <button className={`${cssPrefix}__how-to-play-btn`} onClick={handleShowHowToPlay}>
          How to Play
        </button>
      </header>

      <HowToPlayComponent show={showHowToPlay} onClose={handleCloseHowToPlay} gameId={gameId} />

      <form onSubmit={handleSubmit} className={`${cssPrefix}__guess-form`}>
        <div className={`${cssPrefix}__input-wrapper`}>
          <input
            ref={guessInputRef}
            type="text"
            value={currentGuess}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholderText}
            className={`${cssPrefix}__guess-input`}
            disabled={gameWon}
            autoComplete="off"
          />
          {showSuggestions && (
            <div ref={dropdownRef} className={`${cssPrefix}__suggestions-dropdown`}>
              {suggestions.map((item, index) => (
                <div
                  key={item.name}
                  ref={el => suggestionItemsRef.current[index] = el}
                  className={`${cssPrefix}__suggestion-item ${
                    index === highlightedIndex ? `${cssPrefix}__suggestion-item--highlighted` : ''
                  }`}
                  onMouseDown={() => handleSuggestionClick(item.name)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {item.name}
                </div>
              ))}
            </div>
          )}
        </div>
        <button 
          type="submit" 
          className={`${cssPrefix}__guess-button`}
          disabled={gameWon || !currentGuess.trim() || !isValidGuess}
        >
          <IoMdSend />
        </button>
      </form>

      <div className={`${cssPrefix}__directory-toggle-container ${cssPrefix}__${itemName}-directory-toggle-container`}>
        <button 
          onClick={handleShowDirectory} 
          className={`${cssPrefix}__directory-toggle-button ${cssPrefix}__toggle-${itemName}-directory-button`}
          disabled={gameWon}
        >
          {directoryButtonText}
        </button>
      </div>
      
      {showDirectory && (
        <DirectoryComponent key={gameKey} {...directoryProps} />
      )}

      {currentItem && (
        <HintComponent {...hintProps} />
      )}

      <div className={`${cssPrefix}__game-board-wrapper`}>
        {attempts.length > 0 && (
          <div className={`${cssPrefix}__category-headers`}>
            <div className={`${cssPrefix}__category-header`}>Guess</div>
            {categories.map(cat => {
              const getTooltipContent = (categoryKey) => {
                const tooltipData = {
                  class: ['Ironclad', 'Silent', 'Defect', 'Watcher', 'Colorless'],
                  type: ['Attack', 'Skill', 'Power'],
                  cost: ['0, 1, 2, 3, 4, 5', 'X', 'Unplayable'],
                  keyword: ['Exhaust', 'Retain', 'Innate', 'Ethereal', 'Unplayable'],
                  target: ['Single Enemy', 'All Enemies', 'Self', 'Random Enemy', 'None'],
                  rarity: ['Starter', 'Common', 'Uncommon', 'Rare', 'Special'],
                  size: ['Tiny', 'Small', 'Medium', 'Large', 'Leviathan'],
                  behavior: ['Passive', 'Aggressive', 'Conditional', 'Ruthless'],
                  biome: ['Shallows', 'Kelp Forest', 'Grassy Plateaus', 'Caves', 'Islands', 'Lost River', 'Lava Zone', 'Crater Edge', 'Multiple Biomes'],
                  diet: ['Herbivore', 'Carnivore', 'Scavenger', 'Filter Feeder', 'Unknown'],
                  food_restored: 'Indicates how much food is restored for the Cooked variant of a given creature. N/A for inedible creatures.',
                  testament: ['Old', 'New'],
                  genre: ['Law', 'History', 'Wisdom', 'Prophecy', 'Gospel', 'Epistle', 'Apocalyptic'],
                  author: ['Moses', 'David', 'Solomon', 'Isaiah', 'Jeremiah', 'Unknown', 'And 26 more'],
                  chapter_count: ['1 - 150 chapters'],
                  audience: ['Israel', 'The Church', 'All Nations', 'Individual', 'Nineveh'],
                  age: ['Dark Age', 'Feudal Age', 'Castle Age', 'Imperial Age'],
                  building: ['Town Center', 'Barracks', 'Archery Range', 'Stable', 'Siege Workshop', 'Dock', 'And 4 more'],
                  unit_type: ['Civilian', 'Infantry', 'Archer', 'Cavalry', 'Siege', 'Ship', 'Monk'],
                  range: ['0 - 16 range'],
                  cost: ['Food', 'Wood', 'Gold', 'Food/Gold', 'Food/Wood', 'Wood/Gold'],
                  // Pokemon categories
                  type1: ['Normal', 'Fire', 'Water', 'Grass', 'Electric', 'Ice', 'Fighting', 'Poison', 'Ground', 'Flying', 'Psychic', 'Bug', 'Rock', 'Ghost', 'Dragon'],
                  type2: ['None', 'Fire', 'Water', 'Grass', 'Electric', 'Ice', 'Fighting', 'Poison', 'Ground', 'Flying', 'Psychic', 'Bug', 'Rock', 'Ghost', 'Dragon'],
                  color: ['Red', 'Blue', 'Green', 'Yellow', 'Brown', 'Purple', 'Pink', 'Gray', 'White'],
                  evolution_stage: ['1', '2', '3'],
                  evolution_method: ['Level', 'Stone', 'Trade', 'None'],
                  height_m: ['0.1 - 20.0 meters'],
                  weight_kg: ['0.1 - 999.9 kilograms']
                };

                const options = tooltipData[categoryKey];
                if (!options) return null;

                // Handle string tooltips (descriptive text)
                if (typeof options === 'string') {
                  return (
                    <div style={{ textTransform: 'none' }}>
                      <div style={{ fontSize: '0.85rem', lineHeight: '1.4', textAlign: 'left' }}>
                        {options}
                      </div>
                    </div>
                  );
                }

                // Handle array tooltips (list of possible values)
                const title = `Possible ${cat.label.toLowerCase()} values:`;

                return (
                  <div style={{ textTransform: 'none' }}>
                    <div style={{ marginBottom: '0.5rem', fontWeight: '600' }}>
                      {title}
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '0.85rem', lineHeight: '1.4', textAlign: 'left', display: 'inline-block' }}>
                        {options.map((option, index) => (
                          <span key={option}>
                            • {option}
                            {index < options.length - 1 && <br />}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              };

              const tooltipContent = getTooltipContent(cat.key);
              
              const HeaderComponent = (
                <div className={`${cssPrefix}__category-header`}>
                  {cat.label}
                </div>
              );

              if (tooltipContent) {
                return (
                  <Tooltip
                    key={cat.key}
                    content={tooltipContent}
                    position="bottom"
                    maxWidth="210px"
                  >
                    {HeaderComponent}
                  </Tooltip>
                );
              }

              return <React.Fragment key={cat.key}>{HeaderComponent}</React.Fragment>;
            })}
          </div>
        )}
        
        <div className={`${cssPrefix}__attempts`}>
          {attempts.map((attempt, index) => (
            <div key={index} className={`${cssPrefix}__attempt-row`}>
              <div 
                className={`${cssPrefix}__attempt-cell ${cssPrefix}__attempt-cell--guess-name ${cssPrefix}__attempt-cell--${
                  attempt[itemName].name.toLowerCase() === currentItem?.name.toLowerCase() ? 'correct' : 'incorrect'
                }`}
                style={{ animationDelay: `0s` }}
              >
                {attempt[itemName].name}
              </div>
              {categories.map((cat, catIndex) => (
                <div 
                  key={cat.key} 
                  className={`${cssPrefix}__attempt-cell ${cssPrefix}__attempt-cell--${attempt.results[cat.key]}`}
                  style={{ animationDelay: `${(catIndex + 1) * 0.1}s` }}
                >
                  {cat.key === 'height_m' && attempt.results[cat.key] === 'partial-up' && <span className={`${cssPrefix}__arrow-indicator`}>↑</span>}
                  {cat.key === 'height_m' && attempt.results[cat.key] === 'partial-down' && <span className={`${cssPrefix}__arrow-indicator`}>↓</span>}
                  {cat.key === 'weight_kg' && attempt.results[cat.key] === 'partial-up' && <span className={`${cssPrefix}__arrow-indicator`}>↑</span>}
                  {cat.key === 'weight_kg' && attempt.results[cat.key] === 'partial-down' && <span className={`${cssPrefix}__arrow-indicator`}>↓</span>}
                  {getDisplayValue(attempt[itemName], cat.key)}
                  {cat.key === 'chapter_count' && attempt.results[cat.key] === 'partial-up' && <span className={`${cssPrefix}__arrow-indicator`}>↑</span>}
                  {cat.key === 'chapter_count' && attempt.results[cat.key] === 'partial-down' && <span className={`${cssPrefix}__arrow-indicator`}>↓</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {gameWon && currentItem && (
        <div className={`${cssPrefix}__data-correction-container`}>
          <button 
            onClick={() => setShowDataCorrectionModal(true)}
            className={`${cssPrefix}__data-correction-button`}
          >
            Spot incorrect card data? Let us know!
          </button>
        </div>
      )}

      <div className={`${cssPrefix}__indicator-buffer ${cssPrefix}__color-indicator-buffer`}>
        <IndicatorsPanel 
          show={showIndicators && !gameWon}
          onClose={handleCloseIndicators} 
        />
      </div>

      {gameWon && currentItem && (
        <div className={`${cssPrefix}__victory-message`}>
          <h2>Victory!</h2>
          <div className={`${cssPrefix}__${itemName}-reveal`}>
            <span className={`${cssPrefix}__${itemName}-name`}>{currentItem.name}</span>
            {renderVictoryDetails && renderVictoryDetails(currentItem)}
          </div>
          <button onClick={resetGame} className={`${cssPrefix}__play-again-button`}>
            Play Again
          </button>
          {isDailyMode && dailyCompleted && (
            <div className={`${cssPrefix}__daily-completed-message`}>
              <p>You can keep playing, but it won't count towards your daily streak.</p>
            </div>
          )}
        </div>
      )}

      {gameWon && currentItem && (
        <div className={`${cssPrefix}__share-actions-container`}>
          {(isDailyMode || user?.role === 'owner') && (
            <button 
              onClick={() => setShowShareResultsModal(true)}
              className={`${cssPrefix}__share-results-button`}
            >
              Share Results
            </button>
          )}
        </div>
      )}

      <VictoryConfetti 
        isTriggered={triggerConfetti} 
        onComplete={() => setTriggerConfetti(false)}
        targetSelector={`.${cssPrefix}__victory-message`}
      />

      <ShareResultsModal
        isOpen={showShareResultsModal}
        onClose={() => setShowShareResultsModal(false)}
        attempts={attempts}
        currentItem={currentItem}
        categories={categories}
        gameName={gameName}
        itemName={itemName}
        isDailyMode={isDailyMode}
        todayDate={todayDate}
        currentStreak={currentStreak}
        gameRoute={config.route}
      />

      <DataCorrectionModal
        isOpen={showDataCorrectionModal}
        onClose={() => setShowDataCorrectionModal(false)}
        attempts={attempts}
        currentItem={currentItem}
        categories={categories}
        gameName={gameName}
        itemName={itemName}
      />
    </div>
  );
};

export default GameEngine;